import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Alert, Box, Pagination } from "@mui/material";
import TabNavigation from "../components/TapNavigation";
import InitiateMovementForm from "../components/InitiateMovementForm";
import StatsCards from "../components/StatsCards";
import FilterBar from "../components/FilterBar";
import MovementCard from "../components/MovementCard";
import EmptyState from "../components/EmptyState";
import { getBatteryMovements } from "../../../hooks/use-battery-movement";
import { useQuery } from "@tanstack/react-query";
import useCSVDownload from "../../../hooks/use-movements-data-csv";
import { BatteryMovementSkeleton } from "../components/BatteryMovementSkeleton";
import { MovementCardSkeleton } from "../components/MovementCardSkeleton"; // New component for movement cards skeleton
import { isEqual } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";

const defaultFilters = {
  status: "in-transit",
  initiatedBy: "",
  batteryId: "",
  fromState: "",
  toState: "",
  startDate: "",
  endDate: "",
};

const defaultPagination = {
  limit: 10,
  page: 1,
};

export default function BatteryMovementApp() {
  const { downloadCSV } = useCSVDownload();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const movementView = queryParams.get("view");

  const [manualLoading, setManualLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filters, setFilters] = useState(defaultFilters);
  const [pagination, setPagination] = useState(defaultPagination);
  const [page, setPage] = useState(1);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["batteryMovements", pagination, filters],
    queryFn: getBatteryMovements,
  });

  const movements = useMemo(() => {
    return data?.data || [];
  }, [data?.data]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      page: page,
    }));
    refetch();
  }, [page, refetch]);

  useEffect(() => {
    refetch().finally(() => {
      setFilterLoading(false);
    });
  }, [filters, refetch]);

  const handleNavigate = (q) => {
    if (q === "logs") navigate(`/battery-movement?view=logs`);
    else navigate(`/battery-movement`);
  };

  const handleFilters = useCallback((name, value) => {
    setPage(1);
    setFilterLoading(true);
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilterLoading(true);
    setFilters(defaultFilters);
    setPagination(defaultPagination);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  // Memoize stats calculation
  const stats = useMemo(() => {
    if (!data) {
      return {
        inTransitCount: 0,
        receivedCount: 0,
        cancelledCount: 0,
        rejectedCount: 0,
        totalCount: 0,
      };
    }

    const statusCounts = data.statusCounts || {};

    return {
      inTransitCount: statusCounts["in-transit"] || 0,
      receivedCount: statusCounts["received"] || 0,
      cancelledCount: statusCounts["cancelled"] || 0,
      rejectedCount: statusCounts["rejected"] || 0,
      totalCount: data.totalCount || 0,
    };
  }, [data]);

  const {
    inTransitCount,
    receivedCount,
    cancelledCount,
    rejectedCount,
    totalCount,
  } = stats;

  const handleDownload = useCallback(() => {
    downloadCSV(movements, "battery_movements_data.csv");
  }, [downloadCSV, movements]);

  const handleRefresh = useCallback(async () => {
    setManualLoading(true);
    try {
      await refetch({ throwOnError: false });
    } finally {
      setManualLoading(false);
    }
  }, [refetch]);

  const hasActiveFilter = useMemo(() => {
    return !isEqual(defaultFilters, filters);
  }, [filters]);

  const paginationCount = useMemo(() => {
    return Math.ceil(totalCount / pagination.limit);
  }, [totalCount, pagination.limit]);

  const movementSkeletons = useMemo(() => {
    return Array.from({ length: pagination.limit }, (_, index) => (
      <MovementCardSkeleton key={`skeleton-${index}`} />
    ));
  }, [pagination.limit]);

  const movementCards = useMemo(() => {
    return movements.map((movement) => (
      <MovementCard key={movement.id} movement={movement} refetch={refetch} />
    ));
  }, [movements, refetch]);

  const showInitialLoading = isLoading && !manualLoading && !filterLoading;

  const showMovementSkeletons = manualLoading || filterLoading;

  return (
    <>
      <TabNavigation
        activeTab={movementView || ""}
        onTabChange={handleNavigate}
        inTransitCount={inTransitCount}
      />

      <Box sx={{ p: 0, py: 3 }}>
        {movementView === "logs" ? (
          <>
            {showInitialLoading ? (
              <BatteryMovementSkeleton />
            ) : (
              <Stack>
                <StatsCards
                  inTransitCount={inTransitCount}
                  receivedCount={receivedCount}
                  cancelledCount={cancelledCount}
                  rejectedCount={rejectedCount}
                />

                <FilterBar
                  filters={filters}
                  onFilters={handleFilters}
                  hasActiveFilter={hasActiveFilter}
                  onResetFilters={handleResetFilters}
                  inTransitCount={inTransitCount}
                  receivedCount={receivedCount}
                  cancelledCount={cancelledCount}
                  rejectedCount={rejectedCount}
                  totalCount={totalCount}
                  defaultFilters={defaultFilters}
                  onDownload={handleDownload}
                  onRefresh={handleRefresh}
                />

                {totalCount === 0 && !showMovementSkeletons ? (
                  <EmptyState
                    status={filters.status}
                    onClearFilters={handleResetFilters}
                  />
                ) : (
                  <>
                    {filters.status === "in-transit" && totalCount > 0 && (
                      <Alert severity="warning">
                        {inTransitCount} batteries awaiting receipt
                      </Alert>
                    )}

                    {/* Show movement card skeletons when refreshing, otherwise show actual cards */}
                    {showMovementSkeletons ? movementSkeletons : movementCards}
                  </>
                )}

                <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                  <Pagination
                    count={paginationCount}
                    page={page}
                    onChange={handlePageChange}
                    color="secondary"
                  />
                </Box>
              </Stack>
            )}
          </>
        ) : (
          <InitiateMovementForm refetch={refetch} />
        )}
      </Box>
    </>
  );
}
